'use client';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps } from '@/app/types';
import Button from '@/components/atoms/button';
import { Divider } from '@/components/atoms/divider';
import {
  CodeBlockItem,
  CodeBlockItemProps,
} from '@/components/organisms/codeBlock/CodeBlockItem';
import MaxHeightCalcWrapper from '../utils/MaxHeightCalcWrapper';

export type CodeBlockProps = {
  items: {
    code: CodeBlockItemProps['code'];
    language: CodeBlockItemProps['language'];
  }[];
  showLineNumbers?: CodeBlockItemProps['showLineNumbers'];
  showCopyButton?: CodeBlockItemProps['showCopyButton'];
  classNames?: {
    root?: string;
    languageTab?: string;
    codeBlockItem?: CodeBlockItemProps['classNames'];
  };
} & HTMLDataTrackerProps;

const maxContainerHeightPx = 500;
const verticalPaddingPx = 36;

export function CodeBlock({
  items,
  showLineNumbers,
  showCopyButton,
  classNames,
  ...rest
}: CodeBlockProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);

  const handleMaxHeightCalculated = (h: number) => {
    const maxAllowed = maxContainerHeightPx - 2 * verticalPaddingPx;

    const height = h > maxAllowed ? maxAllowed : h;
    // compensate for padding of py-9
    setMaxHeight(height);
  };

  function handleTabChange(index: number) {
    setActiveTab(index);
  }

  const RenderCodeBlockItem = (
    item: CodeBlockItemProps,
    ContainerProps?: CodeBlockItemProps['ContainerProps']
  ) => (
    <CodeBlockItem
      key={item.language}
      code={item.code}
      language={item.language}
      showLineNumbers={showLineNumbers}
      showCopyButton={showCopyButton}
      classNames={{
        root: cn('overflow-y-auto', classNames?.root),
        ...classNames?.codeBlockItem,
        copyIcon: cn(
          'text-neutral-dark-N00',
          classNames?.codeBlockItem?.copyIcon
        ),
      }}
      ContainerProps={ContainerProps}
    />
  );

  const languages = items.map((item) => item.language);
  const showTabs = languages.length > 1;
  return (
    <>
      <div
        className='relative w-full'
        data-tracker-component='code-block'
        {...rest}
      >
        <div
          style={{ maxHeight: maxContainerHeightPx }}
          className={cn(
            'bg-neutral-dark-N99 dark:border-neutral-dark-N80 flex w-full flex-col gap-6 overflow-auto rounded-xl px-6 md:flex-row dark:border dark:border-solid',
            // when changing this padding, make sure to update verticalPaddingPx to match
            'py-9',
            classNames?.root
          )}
        >
          {showTabs && (
            <>
              <div
                className='flex flex-row gap-4 md:flex-col'
                data-tracker-component='tab'
              >
                {languages.map((language, index) => (
                  <Button
                    variant='text'
                    color='tertiary'
                    key={index}
                    slim
                    onClick={() => handleTabChange(index)}
                    classNames={{
                      root: cn(
                        'hover:underline text-neutral-dark-N00',
                        activeTab !== index
                          ? 'text-neutral-dark-N40'
                          : 'underline',
                        classNames?.languageTab
                      ),
                    }}
                  >
                    {language}
                  </Button>
                ))}
              </div>
              <div className='hidden md:block'>
                <Divider
                  alignment='vertical'
                  showOnDark
                  classNames={{
                    root: 'border-neutral-N40 dark:border-neutral-dark-N80 h-full',
                  }}
                />
              </div>
              <div className='block md:hidden'>
                <Divider
                  alignment='horizontal'
                  showOnDark
                  classNames={{
                    root: 'border-neutral-N40 dark:border-neutral-dark-N80',
                  }}
                />
              </div>
            </>
          )}
          {RenderCodeBlockItem(items[activeTab], {
            style: {
              height: maxHeight,
            },
          })}
        </div>
      </div>
      <MaxHeightCalcWrapper onCalculated={handleMaxHeightCalculated}>
        {items.map((item) => RenderCodeBlockItem(item))}
      </MaxHeightCalcWrapper>
    </>
  );
}
